import React, { useRef, useCallback, useState } from 'react';
import Webcam from 'react-webcam';
import { motion } from 'framer-motion';
import { Camera, Scan, Zap, Target } from 'lucide-react';
import GlassCard from '../UI/GlassCard';
import { useFoodAnalysis } from '../../contexts/FoodAnalysisContext';

interface CameraViewProps {
  onCapture: (imageSrc: string) => void;
  isAnalyzing: boolean;
}

const CameraView: React.FC<CameraViewProps> = ({ onCapture, isAnalyzing }) => {
  const webcamRef = useRef<Webcam>(null);
  const [isScanning, setIsScanning] = useState(false);
  const { analyzeImage } = useFoodAnalysis();

  const capture = useCallback(async () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      setIsScanning(true);
      onCapture(imageSrc);
      
      // Convert base64 to image for analysis
      const img = new Image();
      img.onload = async () => {
        try {
          await analyzeImage(img);
        } catch (error) {
          console.error('Analysis failed:', error);
        } finally {
          setIsScanning(false);
        }
      };
      img.src = imageSrc;
    }
  }, [webcamRef, onCapture, analyzeImage]);

  return (
    <GlassCard className="relative overflow-hidden">
      <div className="relative">
        <Webcam
          ref={webcamRef}
          audio={false}
          screenshotFormat="image/jpeg"
          className="w-full h-96 object-cover rounded-xl"
          videoConstraints={{
            width: 1280,
            height: 720,
            facingMode: "environment"
          }}
        />
        
        {/* Scanning overlay */}
        {(isScanning || isAnalyzing) && (
          <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
            <motion.div
              className="w-64 h-64 border-4 border-purple-500 rounded-lg"
              animate={{
                scale: [1, 1.1, 1],
                opacity: [0.5, 1, 0.5]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <div className="w-full h-full flex items-center justify-center">
                <Scan className="w-12 h-12 text-purple-400 animate-pulse" />
              </div>
            </motion.div>
          </div>
        )}
        
        {/* Corner markers */}
        <div className="absolute top-4 left-4 w-8 h-8 border-l-4 border-t-4 border-purple-400 rounded-tl-lg" />
        <div className="absolute top-4 right-4 w-8 h-8 border-r-4 border-t-4 border-purple-400 rounded-tr-lg" />
        <div className="absolute bottom-4 left-4 w-8 h-8 border-l-4 border-b-4 border-purple-400 rounded-bl-lg" />
        <div className="absolute bottom-4 right-4 w-8 h-8 border-r-4 border-b-4 border-purple-400 rounded-br-lg" />
        
        {/* Center target */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <Target className="w-8 h-8 text-purple-400 opacity-50" />
        </div>
      </div>
      
      {/* Controls */}
      <div className="p-6">
        <div className="flex justify-center space-x-4">
          <motion.button
            onClick={capture}
            disabled={isAnalyzing || isScanning}
            className="flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-full font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {isAnalyzing || isScanning ? (
              <>
                <Zap className="w-5 h-5 animate-pulse" />
                <span>Analyzing...</span>
              </>
            ) : (
              <>
                <Camera className="w-5 h-5" />
                <span>Scan Food</span>
              </>
            )}
          </motion.button>
        </div>
        
        <p className="text-center text-white/70 mt-4 text-sm">
          Point your camera at food items and tap "Scan Food" for instant analysis
        </p>
      </div>
    </GlassCard>
  );
};

export default CameraView;